import React, { forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import Can from 'components/Can';
import Box from '@material-ui/core/Box';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import Backdrop from '@material-ui/core/Backdrop';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';

import useStyles from './Modal.styled';
import { Tooltip } from '@material-ui/core';
import { SlideInDown } from '../../Modals/Guiding/SlideInDown';

const renderCloseButton = (type, onClick) => {
  if (type === 'icon') {
    return (
      <IconButton data-cy="close-button" color="secondary" aria-label="close" onClick={onClick}>
        <CloseIcon />
      </IconButton>
    );
  }

  return (
    <Button color="primary" onClick={onClick} style={{ fontWeight: 'bold' }}>
      Close
    </Button>
  );
};

const InfoTooltip = ({ title, className }) => {
  return (
    <Tooltip title={title} className={className}>
      <InfoIcon color="primary" />
    </Tooltip>
  );
};

const RenderEnterButton = ({
  classes,
  enterButtonLabel,
  loading,
  disabled,
  onClick,
  buttonsVariant,
  ...props
}) => (
  <Button
    size="large"
    color="primary"
    variant={buttonsVariant}
    onClick={onClick}
    className={classes.modalBtn}
    disabled={loading || disabled}
    {...props}
  >
    {enterButtonLabel}
  </Button>
);

renderCloseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['label', 'icon']).isRequired,
};

const ModalComponent = ({
  size,
  maxWidth,
  title,
  children,
  open,
  handleClick,
  enterButtonProps,
  enableCloseButton,
  closeButtonType,
  hasEnterButton,
  loading,
  id,
  hasCancelButton,
  onEnter,
  onCancel,
  enterButtonLabel,
  disabled,
  disabledCancelButton,
  cancelButtonLabel,
  hasHeader,
  hasFooter,
  privileges,
  containerCustomClass,
  containerClassName,
  bodyClassName = '',
  footerClassName = '',
  buttonsVariant,
  enterVariant,
  enterTitleToolTip = null,
  cancelTitleToolTip = null,
  ...props
}) => {
  const classes = useStyles();
  return (
    <Modal
      open={open}
      id={id || 'modal'}
      onClose={handleClick}
      className={classes.modal}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      aria-labelledby={`modal-${title}`}
      aria-describedby={`modal-${title}`}
      {...props}
    >
      <SlideInDown in={open}>
        <Box
          className={clsx(classes.modalContainer, classes[`modal${size}`], containerClassName)}
          style={{ maxWidth }}
        >
          {hasHeader && (
            <Box
              className={clsx(
                classes.modalHeader,
                classes.modalHeaderPadding,
                containerCustomClass
              )}
            >
              <h2 data-cy="modalTitle" className={classes.modalHeaderTitle}>
                {title}
              </h2>
              {enableCloseButton && renderCloseButton(closeButtonType, handleClick)}
            </Box>
          )}
          <Box className={clsx(classes.modalBody, bodyClassName)}>{children}</Box>
          {hasFooter && (
            <Box className={clsx(classes.modalFooter, footerClassName)}>
              {hasCancelButton && (
                <Box>
                  <Button
                    data-cy="btn-cancel"
                    size="large"
                    variant={buttonsVariant}
                    color="primary"
                    classes={{
                      root: classes.modalBtn,
                    }}
                    onClick={onCancel}
                    disabled={loading || disabledCancelButton}
                  >
                    {cancelButtonLabel}
                  </Button>
                  {cancelTitleToolTip && (
                    <InfoTooltip
                      title={cancelTitleToolTip}
                      disabled={false}
                      className={classes.tooltipSize}
                    />
                  )}
                </Box>
              )}

              {hasEnterButton && (
                <>
                  {privileges ? (
                    <Can I={privileges?.action} a={privileges?.subject}>
                      <RenderEnterButton
                        onClick={onEnter}
                        classes={classes}
                        buttonsVariant={enterVariant || buttonsVariant}
                        datacy="btn-save"
                        data-cy="btn-save"
                        className={classes.modalBtn}
                        disabled={loading || disabled}
                        enterButtonLabel={enterButtonLabel}
                        {...enterButtonProps}
                      />
                      {enterTitleToolTip && <InfoTooltip title={enterTitleToolTip} />}
                    </Can>
                  ) : (
                    <>
                      <RenderEnterButton
                        onClick={onEnter}
                        classes={classes}
                        buttonsVariant={enterVariant || buttonsVariant}
                        datacy="btn-save"
                        data-cy="btn-save"
                        className={classes.modalBtn}
                        disabled={loading || disabled}
                        enterButtonLabel={enterButtonLabel}
                        {...enterButtonProps}
                      />
                      {enterTitleToolTip && <InfoTooltip title={enterTitleToolTip} />}
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </SlideInDown>
    </Modal>
  );
};

ModalComponent.propTypes = {
  title: PropTypes.string,
  disabled: PropTypes.bool,
  hasHeader: PropTypes.bool,
  hasFooter: PropTypes.bool,
  privileges: PropTypes.object,
  enableCloseButton: PropTypes.bool,
  enterButtonLabel: PropTypes.string,
  cancelButtonLabel: PropTypes.string,
  containerCustomClass: PropTypes.any,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  closeButtonType: PropTypes.oneOf(['label', 'icon']),
  buttonsVariant: PropTypes.string,
  enterVariant: PropTypes.string,
};

ModalComponent.defaultProps = {
  size: 'md',
  disabled: false,
  hasHeader: true,
  hasFooter: true,
  closeButtonType: 'icon',
  enableCloseButton: true,
  enterButtonLabel: 'Done',
  cancelButtonLabel: 'Cancel',
  containerCustomClass: '',
  buttonsVariant: 'text',
};

export default ModalComponent;
